/**
 * TeamMemberPickerModal — checkbox add/remove flow for a project's Team.
 *
 * Always resolves to a single, already-known organization's people checklist:
 * scope='alder-james' looks up the firm's own org by name; scope='client' uses the project's
 * Projects.Client link; scope='subconsultant' always receives `org` directly (from an already
 * AWARDED subconsultant branch's "Add people from X" action — engaging a new subconsultant org at
 * all goes through AddSubconsultantCandidateModal + Award first, see TeamSection.jsx).
 *
 * For scope='alder-james' only, the candidate list is narrowed to Contact Type === 'Team' — the
 * org's Contacts include former employees (Contact Type 'Former Team') and unrelated personal
 * contacts, which shouldn't be addable as current team members. Someone already on the team stays
 * visible (so they can still be removed) even if their type has since drifted.
 *
 * Checking/unchecking a person only stages a local pending change — nothing is written to the
 * Project Teams table until "Done" is clicked, which commits every staged change in one batch.
 * Closing via × with unsaved changes confirms first (they're discarded, not committed).
 */

import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { getContactNamesByIds, getContactTypesByIds } from '../services/contacts.js'
import { addTeamMember, removeTeamMember, isAlderJamesOrg } from '../services/team.js'
import { confirmDialog } from './Dialog.jsx'

const btnBase = { border: 'none', borderRadius: 4, padding: '9px 20px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer' }

export default function TeamMemberPickerModal({ scope, org, projectId, clientOrgId, organizations, teamRows, onClose, onChanged }) {
  const [selectedOrg, setSelectedOrg] = useState(org || null)
  const [names, setNames] = useState({}) // contactId -> resolved name
  const [types, setTypes] = useState({}) // contactId -> Contact Type (only fetched for alder-james)
  const [typesLoaded, setTypesLoaded] = useState(scope !== 'alder-james')
  const [pending, setPending] = useState({}) // contactId -> staged desired-checked state (unsaved)
  const [committing, setCommitting] = useState(false)
  const [error, setError] = useState('')

  // Resolve the fixed org for alder-james / client scopes once data is available.
  useEffect(() => {
    if (scope === 'alder-james') {
      setSelectedOrg((organizations || []).find(isAlderJamesOrg) || null)
    } else if (scope === 'client') {
      setSelectedOrg(clientOrgId ? (organizations || []).find(o => o.id === clientOrgId) || null : null)
    } else {
      setSelectedOrg(org || null)
    }
  }, [scope, org, organizations, clientOrgId])

  // Resolve display names (and, for alder-james, Contact Type) for the selected org's people.
  useEffect(() => {
    if (!selectedOrg) { setNames({}); setTypes({}); setTypesLoaded(scope !== 'alder-james'); return }
    const ids = selectedOrg.fields['Contacts'] || []
    if (!ids.length) { setNames({}); setTypes({}); setTypesLoaded(true); return }
    getContactNamesByIds(ids).then(setNames).catch(() => setNames({}))
    if (scope === 'alder-james') {
      setTypesLoaded(false)
      getContactTypesByIds(ids).then(t => { setTypes(t); setTypesLoaded(true) }).catch(() => { setTypes({}); setTypesLoaded(true) })
    } else {
      setTypes({}); setTypesLoaded(true)
    }
  }, [selectedOrg, scope])

  const rowByContact = useMemo(() => {
    const m = {}
    ;(teamRows || []).forEach(r => { const cid = (r.fields['Contact'] || [])[0]; if (cid) m[cid] = r.id })
    return m
  }, [teamRows])

  const pendingCount = Object.keys(pending).length

  if (!projectId) return null

  function isChecked(contactId) {
    return Object.prototype.hasOwnProperty.call(pending, contactId) ? pending[contactId] : !!rowByContact[contactId]
  }

  // Stage the toggle only — nothing is written until Done. Toggling back to the original state
  // drops the pending entry entirely, so an unrelated commit doesn't touch an untouched person.
  function toggle(contactId) {
    const next = !isChecked(contactId)
    setPending(p => {
      const copy = { ...p }
      if (next === !!rowByContact[contactId]) delete copy[contactId]
      else copy[contactId] = next
      return copy
    })
  }

  async function commit() {
    const entries = Object.entries(pending)
    if (!entries.length) { onClose(); return }
    setCommitting(true); setError('')
    const results = await Promise.allSettled(entries.map(([contactId, wantChecked]) => {
      const existingRowId = rowByContact[contactId]
      if (wantChecked && !existingRowId) return addTeamMember(projectId, contactId)
      if (!wantChecked && existingRowId) return removeTeamMember(existingRowId)
      return Promise.resolve()
    }))
    const failed = results.map((r, i) => (r.status === 'rejected' ? entries[i] : null)).filter(Boolean)
    setCommitting(false)
    onChanged() // refresh with whatever succeeded, even on partial failure
    if (failed.length) {
      setPending(Object.fromEntries(failed))
      setError('Could not save ' + failed.length + ' change' + (failed.length !== 1 ? 's' : '') + ' — try again.')
      return
    }
    onClose()
  }

  async function cancelClose() {
    if (committing) return
    if (pendingCount > 0 && !(await confirmDialog({
      title: 'Discard changes?',
      message: `You have ${pendingCount} unsaved change${pendingCount !== 1 ? 's' : ''}. Close without saving?`,
      confirmLabel: 'Discard', danger: true,
    }))) return
    onClose()
  }

  const title = scope === 'alder-james' ? 'Add Alder James team members'
    : scope === 'client' ? 'Add client contacts'
    : selectedOrg ? 'Add people from ' + (selectedOrg.fields['Organization Name'] || 'this organization')
    : 'Add people'

  const allContactIds = selectedOrg ? (selectedOrg.fields['Contacts'] || []) : []
  // Alder James only: narrow to Contact Type === 'Team', but never hide someone already on the
  // team (so an existing, type-drifted member can still be seen/removed). While types are still
  // loading, show everyone rather than flashing an empty list.
  const visibleContactIds = (scope === 'alder-james' && typesLoaded)
    ? allContactIds.filter(cid => types[cid] === 'Team' || !!rowByContact[cid])
    : allContactIds

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="TeamMemberPickerModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 12, border: '0.5px solid var(--aj-border)', width: 420, maxWidth: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 13, color: 'var(--aj-text)' }}>{title}</div>
          <button onClick={cancelClose} disabled={committing} style={{ background: 'transparent', border: 'none', cursor: committing ? 'default' : 'pointer', color: 'var(--aj-text-muted)', padding: 4, opacity: committing ? 0.5 : 1 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div style={{ padding: '14px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {selectedOrg ? (
            <>
              {visibleContactIds.length === 0 && allContactIds.length === 0 && (
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-muted)' }}>
                  {selectedOrg.fields['Organization Name']} has no people in Contacts yet — add them there first.
                </div>
              )}
              {visibleContactIds.length === 0 && allContactIds.length > 0 && (
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-muted)' }}>
                  No current team-type contacts on {selectedOrg.fields['Organization Name']} — check their Contact Type in Contacts.
                </div>
              )}
              {visibleContactIds.map(cid => {
                const checked = isChecked(cid)
                const isStaged = Object.prototype.hasOwnProperty.call(pending, cid)
                return (
                  <label key={cid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 4px', cursor: committing ? 'default' : 'pointer', opacity: committing ? 0.5 : 1 }}>
                    <input type="checkbox" checked={checked} disabled={committing} onChange={() => toggle(cid)} />
                    <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' }}>{names[cid] || '…'}</span>
                    {isStaged && <span style={{ fontSize: 10, color: '#c79a3a', fontFamily: '"DM Sans",sans-serif' }}>{checked ? '+ pending' : '− pending'}</span>}
                  </label>
                )
              })}
            </>
          ) : (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-muted)' }}>
              {scope === 'client'
                ? "This project doesn't have a Client organization set yet — set it on the Project record first."
                : scope === 'alder-james'
                  ? 'Could not find the Alder James organization record.'
                  : 'No organization selected.'}
            </div>
          )}

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#C0392B' }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1 }} />
          <button onClick={commit} disabled={committing} style={{ ...btnBase, background: '#07272D', color: '#fff', opacity: committing ? 0.6 : 1 }}>
            {committing ? 'Saving…' : pendingCount > 0 ? `Done · ${pendingCount} change${pendingCount !== 1 ? 's' : ''}` : 'Done'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
